// Board ENTRANCE choreography — sub-split from boardView.ts (FACE&FURY S6, behavior-frozen).
// Bodies are byte-identical to the monolith; only the containing file changed. Bound onto GameScene
// as class fields via the boardView.ts barrel — do NOT import sibling controllers (S2 rule).
import Phaser from 'phaser';
import { boardReveal } from '../juice';
import { motionAllowed } from '../settings';
import type { GameScene } from '../scenes/GameScene';



  /**
   * Board ENTRANCE choreography (renderer-only). On board start the board is "served", not snapped on:
   *   1) the existing diagonal cell cascade (boardReveal — scale-overshoot via Back.easeOut),
   *   2) a one-shot SHEEN sweep that travels across the grid once (a quick light ripple),
   *   3) a rule-banner SLIDE-IN + fade so the prompt arrives as its own beat.
   * The whole sequence stays under ~700ms so it never delays play; under reduced-motion boardReveal
   * collapses to a quick fade, the sweep is skipped, and the banner appears immediately.
   */
export function runBoardReveal(this: GameScene): void {
    const mapped = this.cellViews
      .map((v, id) =>
        v && v.container.active
          ? { container: v.container, col: id % this.cols, row: Math.floor(id / this.cols) }
          : null,
      )
      .filter((c): c is { container: Phaser.GameObjects.Container; col: number; row: number } => c !== null);
    if (this.boardIndex === 0) {
      boardReveal(this, mapped, { cols: this.cols, perCellMs: 22 });
    } else {
      for (const c of mapped) c.container.setScale(1).setAlpha(1);
    }

    const reduced = !motionAllowed();
    // --- rule-banner slide-in beat -------------------------------------------------
    if (this.bannerBg && this.bannerRule) {
      const targetX = this.bannerRestX;
      if (reduced) {
        this.bannerRule.setAlpha(1).setX(targetX);
      } else {
        this.bannerRule.setAlpha(0).setX(targetX - 18);
        this.tweens.add({
          targets: this.bannerRule,
          x: targetX,
          alpha: 1,
          duration: 320,
          delay: 90,
          ease: 'Back.easeOut',
        });
      }
    }

    if (reduced) return;

    // --- one-shot board sheen sweep ------------------------------------------------
    // A single bright, low-alpha band glides diagonally across the grid once (a served "ripple"),
    // then self-destructs. Created ONCE here (not per-frame); masked to the board bounds via a quick
    // x-travel so it reads as light passing over the slab without touching the labels' contrast.
    const boardH = this.cellH * this.rows;
    const top = this.gridY;
    const left = this.gridX;
    const bandW = Math.max(28, this.boardW * 0.22);
    const sweep = this.add
      .rectangle(left - bandW, top + boardH / 2, bandW, boardH, 0xffffff, 0.12)
      .setDepth(6)
      .setAngle(12)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: sweep,
      x: left + this.boardW + bandW,
      duration: 520,
      delay: 120,
      ease: 'Quad.easeInOut',
      onComplete: () => sweep.destroy(),
    });
  }
